'use client';

import { useEffect, useRef, useState } from 'react';
import { BeatLoader } from 'react-spinners';
import { useRouter, useSearchParams } from 'next/navigation';

import { CardWrapper } from './card-wrapper';
import { FormError, FormSuccess } from '@/components/form';
import { newVerification } from '@/actions';

export const NewVerificationForm = () => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const token = searchParams.get('token');

  // 👇 derive initial error instead of setting it in an effect
  const [error, setError] = useState<string | undefined>(() =>
    token ? '' : 'Missing token!'
  );
  const [success, setSuccess] = useState<string | undefined>('');

  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // No token? Do nothing. (error already set by initialiser)
    if (!token) return;

    let cancelled = false;

    (async () => {
      try {
        const data = await newVerification(token);
        if (cancelled) return;

        if (data?.success) {
          setSuccess(data.success);
          timeoutIdRef.current = setTimeout(() => {
            if (!cancelled) {
              // If password setup is needed, redirect to password setup page
              if (data?.needsPasswordSetup && data?.passwordToken) {
                router.push(`/new-password?token=${data.passwordToken}`);
              } else {
                router.push('/login');
              }
            }
          }, 1500);
        } else {
          setError(data?.error ?? 'Verification failed.');
        }
      } catch {
        if (!cancelled) setError('Something went wrong!');
      }
    })();

    return () => {
      cancelled = true;
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [token, router]);

  if (!token) return <FormError message="Missing token!" />;

  return (
    <CardWrapper
      headerLabel="Confirming your verification"
      backButtonHref="/login"
      backButtonLabel="Back to login"
    >
      <div className="flex flex-col gap-5 items-center w-full justify-center">
        {!success && !error && <BeatLoader />}
        <FormSuccess message={success} />
        <FormError message={error} />
      </div>
    </CardWrapper>
  );
};
